/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
==========================================================================================*/


import Vue from 'vue'
import Router from 'vue-router'
import store from '../store/store.js'

Vue.use(Router);

const router = new Router({
    mode: 'history',
    base: '/',
    routes: [
        {
            // =============================================================================
            // BACKEND AUTH ROUTES
            // =============================================================================
            path: '/system',
            component: () => import('../layouts/main/Main.vue'),
            meta: {
                requiresAuth: true
            },
            children: [
                {
                    path: '/system/dashboard',
                    name: 'navigation.dashboard',
                    component: () => import('../views/pages/dashboard/Dashboard.vue'),
                    meta: {}
                }, {
                    path: '/system/organization',
                    name: 'navigation.organization',
                    component: () => import('../views/pages/organization/Organization.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.organization', active: true},
                        ],
                        pageTitle: 'navigation.organization',
                    }
                }, {
                    path: '/system/organization/users',
                    name: 'navigation.organization.users',
                    component: () => import('../views/pages/organization/users/Users.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.organization', url: '/system/organization'},
                            {title: 'navigation.organization.users', active: true},
                        ],
                        pageTitle: 'navigation.organization.users',
                    }
                }, {
                    path: '/system/organization/users/create',
                    name: 'navigation.organization.users.create',
                    component: () => import('../views/pages/organization/users/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.organization', url: '/system/organization'},
                            {title: 'navigation.organization.users', url: '/system/organization/users'},
                            {title: 'pages.users.create', active: true},
                        ],
                        pageTitle: 'navigation.organization.users',
                    }
                }, {
                    path: '/system/organization/users/view/:id',
                    name: 'navigation.organization.users.view',
                    component: () => import('../views/pages/organization/users/View.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.organization', url: '/system/organization'},
                            {title: 'navigation.organization.users', url: '/system/organization/users'},
                            {title: 'pages.users.view', active: true},
                        ],
                        pageTitle: 'navigation.organization.users',
                    }
                }, {
                    path: '/system/organization/users/edit/:id',
                    name: 'navigation.organization.users.edit',
                    component: () => import('../views/pages/organization/users/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.organization', url: '/system/organization'},
                            {title: 'navigation.organization.users', url: '/system/organization/users'},
                            {title: 'pages.users.edit', active: true},
                        ],
                        pageTitle: 'navigation.organization.users',
                    }
                }, {
                    path: '/system/organization/roles',
                    name: 'navigation.organization.roles',
                    component: () => import('../views/pages/organization/roles/Roles.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.organization', url: '/system/organization'},
                            {title: 'navigation.organization.roles', active: true},
                        ],
                        pageTitle: 'navigation.organization.roles',
                    }
                }, {
                    path: '/system/organization/roles/create',
                    name: 'navigation.organization.roles.create',
                    component: () => import('../views/pages/organization/roles/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.organization', url: '/system/organization'},
                            {title: 'navigation.organization.roles', url: '/system/organization/roles'},
                            {title: 'pages.roles.create', active: true},
                        ],
                        pageTitle: 'navigation.organization.roles',
                    }
                }, {
                    path: '/system/organization/roles/view/:id',
                    name: 'navigation.organization.roles.view',
                    component: () => import('../views/pages/organization/roles/View.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.organization', url: '/system/organization'},
                            {title: 'navigation.organization.roles', url: '/system/organization/roles'},
                            {title: 'pages.roles.view', active: true},
                        ],
                        pageTitle: 'navigation.organization.roles',
                    }
                }, {
                    path: '/system/organization/roles/edit/:id',
                    name: 'navigation.organization.roles.edit',
                    component: () => import('../views/pages/organization/roles/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.organization', url: '/system/organization'},
                            {title: 'navigation.organization.roles', url: '/system/organization/roles'},
                            {title: 'pages.roles.edit', active: true},
                        ],
                        pageTitle: 'navigation.organization.roles',
                    }
                }, {
                    path: '/system/pages',
                    name: 'navigation.pages',
                    component: () => import('../views/pages/pages/Pages.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.pages', active: true},
                        ],
                        pageTitle: 'navigation.pages',
                    }
                }, {
                    path: '/system/pages/create/:lang_id/:page_id?',
                    name: 'navigation.pages.create',
                    component: () => import('../views/pages/pages/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.pages', url: '/system/pages'},
                            {title: 'pages.pages.create', active: true},
                        ],
                        pageTitle: 'navigation.pages',
                    }
                }, {
                    path: '/system/pages/edit/:id',
                    name: 'navigation.pages.edit',
                    component: () => import('../views/pages/pages/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.pages', url: '/system/pages'},
                            {title: 'pages.pages.edit', active: true},
                        ],
                        pageTitle: 'navigation.pages',
                    }
                }, {
                    path: '/system/menus',
                    name: 'navigation.menus',
                    component: () => import('../views/pages/Menus/Menus.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.menus', active: true},
                        ],
                        pageTitle: 'navigation.menus',
                    }
                }, {
                    path: '/system/menus/edit/:id',
                    name: 'navigation.menus.edit',
                    component: () => import('../views/pages/Menus/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.menus', url: '/system/menus'},
                            {title: 'pages.menus.edit', active: true},
                        ],
                        pageTitle: 'navigation.menus',
                    }
                }, {
                    path: '/system/services',
                    name: 'navigation.services',
                    component: () => import('../views/pages/services/Services.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.services', active: true},
                        ],
                        pageTitle: 'navigation.services',
                    }
                }, {
                    path: '/system/services/create/:lang_id/:service_id?',
                    name: 'navigation.services.create',
                    component: () => import('../views/pages/services/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.services', url: '/system/services'},
                            {title: 'pages.services.create', active: true},
                        ],
                        pageTitle: 'navigation.services',
                    }
                }, {
                    path: '/system/services/edit/:id',
                    name: 'navigation.services.edit',
                    component: () => import('../views/pages/services/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.services', url: '/system/services'},
                            {title: 'pages.services.edit', active: true},
                        ],
                        pageTitle: 'navigation.services',
                    }
                }, {
                    path: '/system/projects',
                    name: 'navigation.projects',
                    component: () => import('../views/pages/projects/Projects.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.projects', active: true},
                        ],
                        pageTitle: 'navigation.projects',
                    }
                }, {
                    path: '/system/projects/create/:lang_id/:project_id?',
                    name: 'navigation.projects.create',
                    component: () => import('../views/pages/projects/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.projects', url: '/system/projects'},
                            {title: 'pages.projects.create', active: true},
                        ],
                        pageTitle: 'navigation.projects',
                    }
                }, {
                    path: '/system/projects/edit/:id',
                    name: 'navigation.projects.edit',
                    component: () => import('../views/pages/projects/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.projects', url: '/system/projects'},
                            {title: 'pages.projects.edit', active: true},
                        ],
                        pageTitle: 'navigation.projects',
                    }
                }, {
                    path: '/system/projects/categories',
                    name: 'navigation.projects.categories',
                    component: () => import('../views/pages/projects/categories/Categories.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.projects', url: '/system/projects'},
                            {title: 'navigation.projects.categories', active: true},
                        ],
                        pageTitle: 'navigation.projects.categories',
                    }
                }, {
                    path: '/system/projects/categories/create/:lang_id/:category_id?',
                    name: 'navigation.projects.categories.create',
                    component: () => import('../views/pages/projects/categories/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.projects', url: '/system/projects'},
                            {title: 'navigation.projects.categories', url: '/system/projects/categories'},
                            {title: 'pages.projects.categories.create', active: true},
                        ],
                        pageTitle: 'navigation.projects.categories',
                    }
                }, {
                    path: '/system/projects/categories/edit/:id',
                    name: 'navigation.projects.categories.edit',
                    component: () => import('../views/pages/projects/categories/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.projects', url: '/system/projects'},
                            {title: 'navigation.projects.categories', url: '/system/projects/categories'},
                            {title: 'pages.projects.categories.edit', active: true},
                        ],
                        pageTitle: 'navigation.projects.categories',
                    }
                }, {
                    path: '/system/partners',
                    name: 'navigation.partners',
                    component: () => import('../views/pages/partners/Partners.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.partners', active: true},
                        ],
                        pageTitle: 'navigation.partners',
                    }
                }, {
                    path: '/system/partners/create',
                    name: 'navigation.partners.create',
                    component: () => import('../views/pages/partners/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.partners', url: '/system/partners'},
                            {title: 'pages.partners.create', active: true},
                        ],
                        pageTitle: 'navigation.partners',
                    }
                }, {
                    path: '/system/partners/edit/:id',
                    name: 'navigation.partners.edit',
                    component: () => import('../views/pages/partners/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.partners', url: '/system/partners'},
                            {title: 'pages.partners.edit', active: true},
                        ],
                        pageTitle: 'navigation.partners',
                    }
                }, {
                    path: '/system/reviews',
                    name: 'navigation.reviews',
                    component: () => import('../views/pages/reviews/Reviews.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.reviews', active: true},
                        ],
                        pageTitle: 'navigation.reviews',
                    }
                }, {
                    path: '/system/reviews/create',
                    name: 'navigation.reviews.create',
                    component: () => import('../views/pages/reviews/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.reviews', url: '/system/reviews'},
                            {title: 'pages.reviews.create', active: true},
                        ],
                        pageTitle: 'navigation.reviews',
                    }
                }, {
                    path: '/system/reviews/edit/:id',
                    name: 'navigation.reviews.edit',
                    component: () => import('../views/pages/reviews/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.reviews', url: '/system/reviews'},
                            {title: 'pages.reviews.edit', active: true},
                        ],
                        pageTitle: 'navigation.reviews',
                    }
                }, {
                    path: '/system/blog/posts',
                    name: 'navigation.blog.posts',
                    component: () => import('../views/pages/blog/Posts.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.blog.posts', active: true},
                        ],
                        pageTitle: 'navigation.blog.posts',
                    }
                }, {
                    path: '/system/blog/posts/create/:lang_id/:post_id?',
                    name: 'navigation.blog.posts.create',
                    component: () => import('../views/pages/blog/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.blog.posts', url: '/system/blog/posts'},
                            {title: 'pages.blog.posts.create', active: true},
                        ],
                        pageTitle: 'navigation.blog.posts',
                    }
                }, {
                    path: '/system/blog/posts/edit/:id',
                    name: 'navigation.blog.posts.edit',
                    component: () => import('../views/pages/blog/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.blog.posts', url: '/system/blog/posts'},
                            {title: 'pages.blog.posts.edit', active: true},
                        ],
                        pageTitle: 'navigation.blog.posts',
                    }
                }, {
                    path: '/system/blog/categories',
                    name: 'navigation.blog.categories',
                    component: () => import('../views/pages/blog/categories/Categories.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.blog.posts', url: '/system/blog/posts'},
                            {title: 'navigation.blog.categories', active: true},
                        ],
                        pageTitle: 'navigation.projects.categories',
                    }
                }, {
                    path: '/system/blog/categories/create/:lang_id/:category_id?',
                    name: 'navigation.blog.categories.create',
                    component: () => import('../views/pages/blog/categories/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.blog.posts', url: '/system/blog/posts'},
                            {title: 'navigation.blog.categories', url: '/system/blog/categories'},
                            {title: 'pages.blog.categories.create', active: true},
                        ],
                        pageTitle: 'navigation.blog.categories',
                    }
                }, {
                    path: '/system/blog/categories/edit/:id',
                    name: 'navigation.blog.categories.edit',
                    component: () => import('../views/pages/blog/categories/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.blog.posts', url: '/system/blog/posts'},
                            {title: 'navigation.blog.categories', url: '/system/blog/categories'},
                            {title: 'pages.blog.categories.edit', active: true},
                        ],
                        pageTitle: 'navigation.blog.categories',
                    }
                }, {
                    path: '/system/shop/products',
                    name: 'navigation.shop.products',
                    component: () => import('../views/pages/shop/Products.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', active: true},
                        ],
                        pageTitle: 'navigation.shop.products',
                    }
                }, {
                    path: '/system/shop/products/create/:lang_id/:product_id?',
                    name: 'navigation.shop.products.create',
                    component: () => import('../views/pages/shop/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'pages.shop.products.create', active: true},
                        ],
                        pageTitle: 'navigation.shop.products',
                    }
                }, {
                    path: '/system/shop/products/edit/:id',
                    name: 'navigation.shop.products.edit',
                    component: () => import('../views/pages/shop/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'pages.shop.products.edit', active: true},
                        ],
                        pageTitle: 'navigation.shop.products',
                    }
                }, {
                    path: '/system/shop/categories',
                    name: 'navigation.shop.categories',
                    component: () => import('../views/pages/shop/categories/Categories.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.categories', active: true},
                        ],
                        pageTitle: 'navigation.shop.categories',
                    }
                }, {
                    path: '/system/shop/categories/create/:lang_id/:category_id?',
                    name: 'navigation.shop.categories.create',
                    component: () => import('../views/pages/shop/categories/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.categories', url: '/system/shop/categories'},
                            {title: 'pages.shop.categories.create', active: true},
                        ],
                        pageTitle: 'navigation.shop.categories',
                    }
                }, {
                    path: '/system/shop/categories/edit/:id',
                    name: 'navigation.shop.categories.edit',
                    component: () => import('../views/pages/shop/categories/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.categories', url: '/system/shop/categories'},
                            {title: 'pages.shop.categories.edit', active: true},
                        ],
                        pageTitle: 'navigation.shop.categories',
                    }
                }, {
                    path: '/system/shop/shipping',
                    name: 'navigation.shop.shipping',
                    component: () => import('../views/pages/shop/shipping/Shippings.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.shipping', active: true},
                        ],
                        pageTitle: 'navigation.shop.shipping',
                    }
                }, {
                    path: '/system/shop/shipping/create/:lang_id/:shipping_id?',
                    name: 'navigation.shop.shipping.create',
                    component: () => import('../views/pages/shop/shipping/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.shipping', url: '/system/shop/shipping'},
                            {title: 'pages.shop.shipping.create', active: true},
                        ],
                        pageTitle: 'navigation.shop.shipping',
                    }
                }, {
                    path: '/system/shop/shipping/edit/:id',
                    name: 'navigation.shop.shipping.edit',
                    component: () => import('../views/pages/shop/shipping/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.shipping', url: '/system/shop/shipping'},
                            {title: 'pages.shop.shipping.edit', active: true},
                        ],
                        pageTitle: 'navigation.shop.shipping',
                    }
                }, {
                    path: '/system/shop/discount-codes',
                    name: 'navigation.shop.discountCodes',
                        component: () => import('../views/pages/shop/discount-codes/DiscountCodes.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.discountCodes', active: true},
                        ],
                        pageTitle: 'navigation.shop.discountCodes',
                    }
                }, {
                    path: '/system/shop/discount-codes/create',
                    name: 'navigation.shop.discountCodes.create',
                    component: () => import('../views/pages/shop/discount-codes/Create.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.discountCodes', url: '/system/shop/discount-codes'},
                            {title: 'pages.shop.discountCodes.create', active: true},
                        ],
                        pageTitle: 'navigation.shop.discountCodes',
                    }
                }, {
                    path: '/system/shop/discount-codes/edit/:id',
                    name: 'navigation.shop.discountCodes.edit',
                    component: () => import('../views/pages/shop/discount-codes/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.discountCodes', url: '/system/shop/discount-codes'},
                            {title: 'pages.shop.discountCodes.edit', active: true},
                        ],
                        pageTitle: 'navigation.shop.discountCodes',
                    }
                }, {
                    path: '/system/shop/orders',
                    name: 'navigation.shop.orders',
                    component: () => import('../views/pages/shop/orders/Orders.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.orders', active: true},
                        ],
                        pageTitle: 'navigation.shop.orders',
                    }
                }, {
                    path: '/system/shop/orders/edit/:id',
                    name: 'navigation.shop.orders.edit',
                    component: () => import('../views/pages/shop/orders/Edit.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.shop.products', url: '/system/shop/products'},
                            {title: 'navigation.shop.orders', url: '/system/shop/orders'},
                            {title: 'pages.shop.orders.edit', active: true},
                        ],
                        pageTitle: 'navigation.shop.orders',
                    }
                }, {
                    path: '/system/settings/general',
                    name: 'navigation.general',
                    component: () => import('../views/pages/settings/General.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.settings'},
                            {title: 'navigation.general', active: true},
                        ],
                        pageTitle: 'navigation.general',
                    }
                }, {
                    path: '/system/settings/languages',
                    name: 'navigation.languages',
                    component: () => import('../views/pages/settings/Languages.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.settings'},
                            {title: 'navigation.languages', active: true},
                        ],
                        pageTitle: 'navigation.languages',
                    }
                }, {
                    path: '/system/settings/translations',
                    name: 'navigation.translations',
                    component: () => import('../views/pages/settings/Translations.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.settings'},
                            {title: 'navigation.translations', active: true},
                        ],
                        pageTitle: 'navigation.translations',
                    }
                }, {
                    path: '/system/profile',
                    name: 'navigation.profile',
                    component: () => import('../views/pages/profile/Profile.vue'),
                    meta: {
                        breadcrumb: [
                            {title: 'navigation.dashboard', url: '/'},
                            {title: 'navigation.profile', active: true},
                        ],
                        pageTitle: 'navigation.profile',
                    }
                },
            ]
        },
        // =============================================================================
        // STATIC ROUTES
        // =============================================================================
        {
            path: '/system',
            component: () => import('../layouts/full-page/FullPage.vue'),
            children: [
                {
                    path: '/system/login/:locale?',
                    name: 'page.login',
                    component: () => import('../views/pages/auth/Login.vue'),
                },
                {
                    path: '/system/create-password/:locale?/:token',
                    name: 'page.create_password',
                    component: () => import('../views/pages/auth/CreatePassword.vue')
                },
                {
                    path: '/system/forgot-password/:locale?',
                    name: 'page.forgot_password',
                    component: () => import('../views/pages/auth/ForgotPassword.vue')
                },
                {
                    path: '/system/reset-password/:locale?/:token',
                    name: 'page.reset_password',
                    component: () => import('../views/pages/auth/ResetPassword.vue')
                },
                {
                    path: '/system/change-password/:locale?/:token',
                    name: 'page.change_password',
                    component: () => import('../views/pages/auth/ChangePassword.vue')
                },
                {
                    path: '/system/change-email/:locale?/:token',
                    name: 'page.change_email',
                    component: () => import('../views/pages/auth/ChangeEmail.vue')
                },
                {
                    path: '/system/confirm/:locale?',
                    name: 'page.confirm',
                    component: () => import('../views/pages/auth/Confirm.vue')
                },
                {
                    path: '/system/sign-up/:locale?',
                    name: 'page.sign_up',
                    component: () => import('../views/pages/auth/SignUp.vue')
                },
                // Redirect to login page, if no match found
                {
                    path: '*',
                    redirect: '/system/login'
                }
            ]
        },
        // =============================================================================
        // website
        // =============================================================================
        {
            path: '/',
            component: () => import('../layouts/website/Page.vue'),
            children: [
                {
                    path: '/:locale?/:slug_one?/:slug_two?/:slug_three?/:slug_four?',
                    name: 'page',
                    component: () => import('../views/pages/website/Page.vue')
                },
            ]
        },
    ],

    scrollBehavior(to, from, savedPosition) {
        return {x: 0, y: 0};
    }
});

router.beforeEach((to, from, next) => {
    if (to.name === 'page') {
        // console.log(from);
        // console.log(to);
        // console.log('router - ' + 1);
        next();
    } else {
        // console.log('router - ' + 2);
        if (to.matched.some(record => record.meta.requiresAuth)) {
            if (store.getters.isLoggedIn) {
                // console.log('router - ' + 3);
                next();
            } else {
                // console.log('router - ' + 4);
                next('/system/login');
            }
        } else {
            if (store.getters.isLoggedIn &&
                (
                    to.name === 'page.login' ||
                    to.name === 'page.forgot_password' ||
                    to.name === 'page.reset_password' ||
                    to.name === 'page.create_password' ||
                    to.name === 'page.change_password' ||
                    to.name === 'page.change_email' ||
                    to.name === 'page.confirm'
                )
            ) {
                // console.log('router - ' + 5);
                next('/system/dashboard');
            } else {
                // console.log('router - ' + 6);
                next();
            }
        }
    }
});

export default router
